import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { useAccountId } from "../../../auth/useAccountId";
import useHasAnyOfScopes from "../../../common/hooks/useHasAnyOfScopes";
import { CORE_API_HOSTNAME } from "../../../env";
import fulfill from "../../../fulfill";
import type { BillingPayoutRule } from "../../../types/billing";

const fetchPayoutRule = async (key: string): Promise<BillingPayoutRule> => {
    const [accountId, , ruleId] = key.split("/");
    return await fulfill.get({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/billing/payout-rules?include_deleted=true`,
        handlers: {
            200: (response: {
                payout_rules: BillingPayoutRule[];
            }) => {
                return response.payout_rules.find((rule) => rule.id === ruleId);
            },
            404: () => null,
        },
    });
};

const fetchDeletePayoutRule = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            ruleId: string;
        };
    },
): Promise<BillingPayoutRule> => {
    const { accountId, ruleId } = options.arg;
    return await fulfill.delete({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/billing/payout-rules/${ruleId}`,
        handlers: {
            200: (response: BillingPayoutRule) => response,
            404: () => null,
        },
    });
};

const fetchPutPayoutRule = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            payoutRule: BillingPayoutRule;
            payoutRuleId: string;
        };
    },
): Promise<BillingPayoutRule> => {
    const { accountId, payoutRule, payoutRuleId } = options.arg;
    return fulfill.put({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/billing/payout-rules/${payoutRuleId}`,
        json: payoutRule,
        handlers: {
            200: (response: BillingPayoutRule) => response,
            404: () => null,
        },
    });
};

export const usePayoutRule = (ruleId: string) => {
    const accountId = useAccountId();
    const canReadBilling = useHasAnyOfScopes(["admin:billing", "read:billing"]);
    const canWriteBilling = useHasAnyOfScopes([
        "admin:billing",
        "write:billing",
    ]);
    const key = `${accountId}/payout-rules/${ruleId}`;
    const {
        data: payoutRule,
        error,
        isLoading,
    } = useSWR(() => (canReadBilling ? key : null), fetchPayoutRule);
    const {
        trigger: deletePayoutRule,
        isMutating: deletingPayoutRule,
        error: deletePayoutRuleError,
        data: deletedPayoutRule,
    } = useSWRMutation(
        () => (canWriteBilling ? key : null),
        fetchDeletePayoutRule,
    );
    const {
        trigger: putPayoutRule,
        isMutating: puttingPayoutRule,
        error: putPayoutRuleError,
        data: putPayoutRuleData,
    } = useSWRMutation(canWriteBilling ? key : null, fetchPutPayoutRule);

    return {
        payoutRule,
        error,
        isLoading,
        deletePayoutRule,
        deletingPayoutRule,
        deletePayoutRuleError,
        deletedPayoutRule,
        putPayoutRule,
        puttingPayoutRule,
        putPayoutRuleError,
        putPayoutRuleData,
    };
};
